import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  ScrollView,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, ClothingItem } from '../types';
import {
  AppHeader,
  ItemCard,
  ItemCardSeparator,
  ITEM_CARD_COMPACT_HEIGHT,
  ITEM_CARD_SEPARATOR_HEIGHT,
  ScreenLayout,
  HomeBand,
  WelcomeHero,
  EmptyStateNoClubs,
  SectionHeader,
  EmptyState,
} from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Spacing, CommonStyles, LAYOUT } from '../constants';
import { itemsService } from '../services/items';
import { useDialog, useUser, useUserClubs } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'Home'>;

/** How many club items the Home grid shows (two rows of four on desktop). */
const RECENT_LIMIT = 8;
/** How many of the user's own listings the hairline list shows. */
const MY_LISTINGS_LIMIT = 5;

// Grid gutters: half the gap is padded onto each card, the row pulls it back.
const DESKTOP_GAP = LAYOUT.grid.desktop.gap;
const MOBILE_GAP = Spacing.md;
const MOBILE_ROW_GAP = Spacing.lg;

export const HomeScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog; // Keep ref in sync

  // Use contexts instead of local state
  const { user } = useUser();
  const { userClubs, loading: clubsLoading } = useUserClubs();

  const [clubItems, setClubItems] = useState<ClothingItem[]>([]);
  const [clubItemTotal, setClubItemTotal] = useState(0);
  const [myListings, setMyListings] = useState<ClothingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newItemCount, setNewItemCount] = useState(0);
  const [currentClubId, setCurrentClubId] = useState('');
  const { isDesktop } = useResponsive();

  const loadHomeData = useCallback(async () => {
    // Don't load data while clubs are still loading to avoid showing wrong empty state
    if (clubsLoading) {
      return;
    }

    try {
      // User and clubs come from context - no need to fetch
      if (user) {
        if (userClubs.length > 0) {
          const clubIds = userClubs.map(uc => uc.club_id);

          // Parallel queries for optimized data loading
          const [clubItemsResult, newItemsResult, myListingsData] = await Promise.all([
            // Query 1: Club items with limit
            itemsService.getItems({
              status: 'available',
              clubIds,
              limit: RECENT_LIMIT,
            }),
            // Query 2: New items from clubs (last 24 hours)
            itemsService.getItems({
              status: 'available',
              clubIds,
              createdAfter: new Date(Date.now() - 24 * 60 * 60 * 1000),
            }),
            // Query 3: User's own listings
            itemsService.getMyListings(),
          ]);

          setClubItems(clubItemsResult.data);
          setClubItemTotal(clubItemsResult.total);
          setNewItemCount(newItemsResult.total);
          setMyListings(myListingsData.slice(0, MY_LISTINGS_LIMIT));
        } else {
          // No clubs - just load user's listings
          const myListingsData = await itemsService.getMyListings();
          setMyListings(myListingsData.slice(0, MY_LISTINGS_LIMIT));
        }
      } else {
        // Not authenticated - clear data
        setMyListings([]);
      }
    } catch (error: any) {
      console.error('[HomeScreen] Failed to load data:', error);
      dialogRef.current.alert(t('common.errors.title'), t('screens.home.loadError'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t, user, userClubs, clubsLoading]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadHomeData();
  }, [loadHomeData]);

  // Navigation handlers
  const handleSearch = (searchTerm: string) => {
    // Merge search term with current club filter
    const params: RootStackParamList['Browse'] = {};
    if (searchTerm) params.search = searchTerm;
    if (currentClubId) params.club_id = currentClubId;
    navigation.navigate('Browse', params);
  };

  const handleClubFilter = (clubId: string, searchTerm: string) => {
    setCurrentClubId(clubId);
    // Merge club filter with current search term (passed from the band toolbar)
    const params: RootStackParamList['Browse'] = {};
    if (searchTerm) params.search = searchTerm;
    if (clubId) params.club_id = clubId;
    navigation.navigate('Browse', params);
  };

  const handleBrowseAll = () => {
    navigation.navigate('Browse', {});
  };

  const handleAddItem = () => {
    navigation.push('ItemForm');
  };

  const openItem = (item: ClothingItem) => navigation.navigate('ItemDetails', { itemId: item.id });

  const clubNames = userClubs
    .map(uc => uc.club?.name)
    .filter((name): name is string => !!name);

  // Straight from the user context, so the greeting is right on the first frame.
  const userName = user?.first_name && user.first_name.trim() !== '' ? user.first_name : 'Guest';

  // The band needs only the clubs; the counts arrive with the home data and
  // show as a skeleton until then, so the band never collapses while loading.
  const showBand = !clubsLoading && userClubs.length > 0;

  // Hairline rows with the line between them (not under the last one), so
  // the row's hover surface can bleed past the content edge.
  const renderCompactList = (items: ClothingItem[]) =>
    Platform.OS === 'web' ? (
      // On web, use simple map to avoid FlatList touch event interference
      <View>
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 ? <ItemCardSeparator /> : null}
            <ItemCard item={item} compact onPress={() => openItem(item)} />
          </React.Fragment>
        ))}
      </View>
    ) : (
      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => <ItemCard item={item} compact onPress={() => openItem(item)} />}
        ItemSeparatorComponent={ItemCardSeparator}
        initialNumToRender={5}
        maxToRenderPerBatch={10}
        windowSize={5}
        removeClippedSubviews={true}
        getItemLayout={(_, index) => ({
          length: ITEM_CARD_COMPACT_HEIGHT,
          offset: (ITEM_CARD_COMPACT_HEIGHT + ITEM_CARD_SEPARATOR_HEIGHT) * index,
          index,
        })}
        scrollEnabled={false}
      />
    );

  /** 4 columns on desktop, 2 on mobile — the gutter is padded onto each card. */
  const renderGrid = (items: ClothingItem[]) => (
    <View style={isDesktop ? styles.gridDesktop : styles.gridMobile}>
      {items.map((item) => (
        <View
          key={item.id}
          style={isDesktop ? styles.gridCellDesktop : styles.gridCellMobile}
        >
          <ItemCard item={item} square={!isDesktop} onPress={() => openItem(item)} />
        </View>
      ))}
    </View>
  );

  return (
    <ScreenLayout
      mobileTab="Home"
      onFabPress={handleAddItem}
      band={
        showBand ? (
          <HomeBand
            loading={loading}
            userName={userName}
            itemCount={clubItemTotal}
            newItemCount={newItemCount}
            userClubCount={userClubs.length}
            userClubs={userClubs}
            clubNames={clubNames}
            selectedClubId={currentClubId}
            onSearch={handleSearch}
            onClubFilter={handleClubFilter}
            onBrowseAll={handleBrowseAll}
          />
        ) : undefined
      }
    >
      <View style={[CommonStyles.container, styles.containerMobile]}>

      {!isDesktop && (
        <AppHeader
          navigation={navigation}
          currentScreen="Home"
          title={t('screens.home.title')}
        />
      )}

      {loading || clubsLoading ? (
        <View style={CommonStyles.centerContainer}>
          <Text>{t('common.loading')}</Text>
        </View>
      ) : userClubs.length === 0 ? (
        <EmptyStateNoClubs navigation={navigation} />
      ) : (
        <ScrollView
          style={styles.content}
          contentContainerStyle={[
            styles.scrollContent,
            isDesktop ? styles.scrollContentDesktop : styles.scrollContentMobile,
          ]}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {!isDesktop && (
            <WelcomeHero
              userName={userName}
              itemCount={clubItemTotal}
              newItemCount={newItemCount}
              userClubCount={userClubs.length}
              onSearch={handleSearch}
            />
          )}

          {/* Section 1: Recent from your clubs */}
          <View style={[styles.section, !isDesktop && styles.sectionMobile]}>
            <SectionHeader
              mobile={!isDesktop}
              title={t('screens.home.recentFromClubs')}
              count={clubItemTotal}
              action={t('screens.home.viewAll')}
              onAction={handleBrowseAll}
            />
            {clubItems.length === 0 ? (
              <EmptyState
                title={t('screens.home.noClubItems')}
                subtitle={t('screens.home.noClubItemsSubtext')}
                actionLabel={t('screens.home.addFirstItem')}
                onAction={handleAddItem}
              />
            ) : (
              renderGrid(clubItems)
            )}
          </View>

          {/* Section 2: My listings */}
          <View style={!isDesktop && styles.sectionMobile}>
            <SectionHeader
              mobile={!isDesktop}
              title={t('screens.home.myListings')}
              count={myListings.length}
              action={myListings.length > 0 ? t('screens.home.viewMyListings') : undefined}
              onAction={() => navigation.navigate('Profile')}
            />
            {myListings.length > 0 ? (
              renderCompactList(myListings)
            ) : (
              <EmptyState
                title={t('screens.home.noMyListings')}
                subtitle={t('screens.home.noMyListingsSubtext')}
                actionLabel={t('screens.home.addFirstItem')}
                onAction={handleAddItem}
              />
            )}
          </View>
        </ScrollView>
      )}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  containerMobile: {
    backgroundColor: Colors.white,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  // The page padding sits on the scrolling content, not on the column, so the
  // scrollbar runs along the column's edge instead of through the gutter.
  scrollContentDesktop: {
    paddingHorizontal: Spacing['3xl'],
    paddingBottom: Spacing['4xl'],
  },
  scrollContentMobile: {
    paddingTop: Spacing.lg,
    paddingHorizontal: Spacing.base,
    paddingBottom: LAYOUT.fab.contentPadding, // Home has the floating "+"
    backgroundColor: Colors.white,
  },
  section: {
    marginBottom: Spacing['3xl'],
  },
  sectionMobile: {
    marginTop: Spacing.xl,
    marginBottom: 0,
  },

  // ── Grids: the gutter is padded onto each cell, the row pulls it back ──────
  gridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -DESKTOP_GAP / 2,
    rowGap: DESKTOP_GAP,
  },
  gridCellDesktop: {
    width: '25%',
    paddingHorizontal: DESKTOP_GAP / 2,
  },
  gridMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -MOBILE_GAP / 2,
    rowGap: MOBILE_ROW_GAP,
  },
  gridCellMobile: {
    width: '50%',
    paddingHorizontal: MOBILE_GAP / 2,
  },
});
